/**
 * Makes the build-time snapshot hydratable.
 *
 * The prerendered HTML is the serialised DOM of a headless browser that rendered the app
 * itself, not the output of `renderToString`. React puts separate text nodes in the DOM for
 * `{name} {level}` — but serialised and parsed again they become one, and hydration, which
 * matches text node by text node, then gives up on the whole page. A server renderer
 * separates such neighbours with an empty comment; this does the same in the live DOM, so
 * the snapshot carries them. Runs only under automation (the prerender), where the page is
 * not seen by anyone, and has no effect on what is rendered.
 */
export function markTextBoundaries(root: HTMLElement): void {
  if (!navigator.webdriver) return;
  const mark = (): void => {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const texts: Text[] = [];
    for (let node = walker.nextNode(); node; node = walker.nextNode()) texts.push(node as Text);
    for (const text of texts) {
      if (text.nextSibling?.nodeType === Node.TEXT_NODE) text.after(document.createComment(""));
    }
  };
  // A microtask after every change, so the last one is marked before anything can read the DOM.
  new MutationObserver(mark).observe(root, { childList: true, subtree: true, characterData: true });
  mark();
}
